export enum Suit {
    HEARTS = 'hearts',
    DIAMONDS = 'diamonds',
    CLUBS = 'clubs',
    SPADES = 'spades'
}

export enum Rank {
    TWO = 2,
    THREE = 3,
    FOUR = 4,
    FIVE = 5,
    SIX = 6,
    SEVEN = 7,
    EIGHT = 8,
    NINE = 9,
    TEN = 10,
    JACK = 11,
    QUEEN = 12,
    KING = 13,
    ACE = 14
}

export interface Card {
    suit: Suit;
    rank: Rank;
    id: string; // e.g., "AS" for Ace of Spades
}

export enum HandType {
    HIGH_CARD = 'high_card',
    PAIR = 'pair',
    TWO_PAIR = 'two_pair',
    THREE_OF_A_KIND = 'three_of_a_kind',
    STRAIGHT = 'straight',
    FLUSH = 'flush',
    FULL_HOUSE = 'full_house',
    FOUR_OF_A_KIND = 'four_of_a_kind',
    STRAIGHT_FLUSH = 'straight_flush',
    ROYAL_FLUSH = 'royal_flush',
    // Deuces Wild specific hands
    FIVE_OF_A_KIND = 'five_of_a_kind',
    WILD_ROYAL_FLUSH = 'wild_royal_flush',
    FOUR_DEUCES = 'four_deuces'
}

export interface HandResult {
    type: HandType;
    payout: number;
    description: string;
    kickers?: Rank[]; // For tie-breaking
    quadRank?: Rank; // For bonus poker quad detection
    isNatural?: boolean; // For distinguishing natural vs wild hands
    winningCardIndices?: number[]; // Indices of cards that form the winning combination
}

export interface VideoPokerState {
    balance: number;
    bet: number;
    hand: Card[];
    heldCards: boolean[]; // Which cards are held
    gamePhase: 'betting' | 'deal' | 'draw' | 'evaluate';
    lastWin: number;
    sessionWins: number;
    sessionLosses: number;
}

export interface VideoPokerConfig {
    minBet: number;
    maxBet: number;
    defaultBet: number;
    paytable: Record<HandType, number[]>; // Payouts for 1-5 coins
}

// Jacks or Better paytable (coins wagered: 1, 2, 3, 4, 5)
export const JACKS_OR_BETTER_PAYTABLE: Record<HandType, number[]> = {
    [HandType.HIGH_CARD]: [0, 0, 0, 0, 0],
    [HandType.PAIR]: [1, 2, 3, 4, 5], // Jacks or Better
    [HandType.TWO_PAIR]: [2, 4, 6, 8, 10],
    [HandType.THREE_OF_A_KIND]: [3, 6, 9, 12, 15],
    [HandType.STRAIGHT]: [4, 8, 12, 16, 20],
    [HandType.FLUSH]: [6, 12, 18, 24, 30],
    [HandType.FULL_HOUSE]: [9, 18, 27, 36, 45],
    [HandType.FOUR_OF_A_KIND]: [25, 50, 75, 100, 125],
    [HandType.STRAIGHT_FLUSH]: [50, 100, 150, 200, 250],
    [HandType.ROYAL_FLUSH]: [250, 500, 750, 1000, 4000], // Bonus for max bet
    [HandType.FIVE_OF_A_KIND]: [0, 0, 0, 0, 0], // Not used in Jacks or Better
    [HandType.WILD_ROYAL_FLUSH]: [0, 0, 0, 0, 0], // Not used in Jacks or Better
    [HandType.FOUR_DEUCES]: [0, 0, 0, 0, 0] // Not used in Jacks or Better
};

// For Jacks or Better, pairs need to be Jacks or higher
export const QUALIFYING_PAIR_RANKS = [Rank.JACK, Rank.QUEEN, Rank.KING, Rank.ACE];